import {
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	Flag,
	Mail,
	Search,
	ShieldAlert,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson, sendJson } from "../api/rest";
import { PageHeader } from "../components/layout/PageHeader";

type IssueStatus = "new" | "in_progress" | "resolved" | "dismissed" | "spam";

interface IssueReport {
	id: number;
	pageType: "product" | "category";
	productId: number | null;
	productName: string | null;
	productSlug: string | null;
	categoryId: number | null;
	categoryName: string | null;
	pagePath: string;
	pageUrl: string;
	pageTitle: string;
	kind: string;
	message: string;
	email: string | null;
	reporterReports: number;
	status: IssueStatus;
	adminNote: string | null;
	handledBy: string | null;
	handledAt: string | null;
	createdAt: string;
}

interface IssueReportList {
	items: IssueReport[];
	total: number;
	page: number;
	totalPages: number;
	counts: Record<IssueStatus, number>;
}

const STATUS_LABEL: Record<IssueStatus, string> = {
	new: "New",
	in_progress: "In progress",
	resolved: "Resolved",
	dismissed: "Dismissed",
	spam: "Spam",
};

const STATUS_DOT: Record<IssueStatus, string> = {
	new: "bg-brand",
	in_progress: "bg-sky-500",
	resolved: "bg-success",
	dismissed: "bg-zinc-400",
	spam: "bg-danger",
};

const KIND_LABEL: Record<string, string> = {
	price: "Wrong price",
	stock: "Wrong stock status",
	info: "Wrong information",
	image: "Wrong image",
	link: "Shop link broken",
	category: "Wrong category",
	wrong_products: "Products that don't belong",
	missing: "Missing products",
	filters: "Filters broken",
	other: "Other",
};

const STATUSES: IssueStatus[] = [
	"new",
	"in_progress",
	"resolved",
	"dismissed",
	"spam",
];

const formatDate = (value: string | null) =>
	value
		? new Date(value).toLocaleString("da-DK", {
				dateStyle: "medium",
				timeStyle: "short",
			})
		: "";

const StatusDot = ({ status }: { status: IssueStatus }) => (
	<span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
		<span className={`size-2 rounded-full ${STATUS_DOT[status]}`} />
		{STATUS_LABEL[status]}
	</span>
);

const ReportCard = ({
	report,
	onChanged,
}: {
	report: IssueReport;
	onChanged: () => void;
}) => {
	const [note, setNote] = useState(report.adminNote ?? "");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const run = async (action: () => Promise<unknown>) => {
		setBusy(true);
		setError(null);
		try {
			await action();
			onChanged();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	};

	const setStatus = (status: IssueStatus) =>
		run(() => sendJson(`/issue-reports/${report.id}`, "PATCH", { status }));

	const saveNote = () =>
		run(() =>
			sendJson(`/issue-reports/${report.id}`, "PATCH", {
				adminNote: note.trim() ? note : null,
			}),
		);

	const spamReporter = () => {
		if (
			!window.confirm(
				`Mark all ${report.reporterReports} open reports from this sender as spam?`,
			)
		)
			return;
		run(() =>
			sendJson(`/issue-reports/${report.id}/spam-reporter`, "POST", {}),
		);
	};

	const subject =
		report.pageType === "product"
			? (report.productName ?? report.pageTitle)
			: (report.categoryName ?? report.pageTitle);

	return (
		<article className="rounded-2xl border border-zinc-200 bg-white p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0 space-y-1">
					<p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">
						{report.pageType === "product" ? "Product" : "Category"} ·{" "}
						{KIND_LABEL[report.kind] ?? report.kind} ·{" "}
						{formatDate(report.createdAt)}
					</p>
					<h3 className="truncate text-sm font-semibold text-zinc-900">
						{subject || report.pagePath}
					</h3>
					<div className="flex flex-wrap items-center gap-3 text-xs">
						<a
							href={report.pageUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900"
						>
							{report.pagePath}
							<ExternalLink className="h-3 w-3" />
						</a>
						{report.productId && (
							<Link
								to={`/products/${report.productId}`}
								className="text-zinc-500 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
							>
								Open product #{report.productId}
							</Link>
						)}
					</div>
				</div>
				<StatusDot status={report.status} />
			</div>

			<p className="mt-4 whitespace-pre-wrap break-words text-sm text-zinc-800">
				{report.message}
			</p>

			<div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
				{report.email ? (
					<a
						href={`mailto:${report.email}`}
						className="inline-flex items-center gap-1.5 hover:text-zinc-900"
					>
						<Mail className="h-3.5 w-3.5" />
						{report.email}
					</a>
				) : (
					<span>No email given</span>
				)}
				{report.reporterReports > 1 && (
					<span className="inline-flex items-center gap-1.5">
						<span className="size-1.5 rounded-full bg-warning" />
						{report.reporterReports} reports from this sender
					</span>
				)}
				{report.handledBy && (
					<span>
						Last changed by {report.handledBy} · {formatDate(report.handledAt)}
					</span>
				)}
			</div>

			<div className="mt-4 flex flex-col gap-2">
				<textarea
					value={note}
					onChange={(e) => setNote(e.target.value)}
					placeholder="Internal note"
					rows={2}
					className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-400"
				/>
			</div>

			<div className="mt-3 flex flex-wrap items-center gap-2">
				{STATUSES.filter((s) => s !== report.status).map((s) => (
					<button
						key={s}
						type="button"
						disabled={busy}
						onClick={() => setStatus(s)}
						className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:border-zinc-400 hover:text-zinc-900 disabled:opacity-50"
					>
						<span className={`size-1.5 rounded-full ${STATUS_DOT[s]}`} />
						{STATUS_LABEL[s]}
					</button>
				))}
				{note !== (report.adminNote ?? "") && (
					<button
						type="button"
						disabled={busy}
						onClick={saveNote}
						className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-50"
					>
						Save note
					</button>
				)}
				{report.reporterReports > 1 && (
					<button
						type="button"
						disabled={busy}
						onClick={spamReporter}
						className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-danger hover:bg-red-50 disabled:opacity-50"
					>
						<ShieldAlert className="h-3.5 w-3.5" />
						Mark sender as spam
					</button>
				)}
			</div>
			{error && <p className="mt-2 text-xs text-danger">{error}</p>}
		</article>
	);
};

const IssueReports = () => {
	const [data, setData] = useState<IssueReportList | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<IssueStatus | "">("new");
	const [pageType, setPageType] = useState("");
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [page, setPage] = useState(1);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({ page: String(page) });
			if (status) params.set("status", status);
			if (pageType) params.set("pageType", pageType);
			if (search) params.set("q", search);
			setData(await fetchJson<IssueReportList>(`/issue-reports?${params}`));
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}, [page, status, pageType, search]);

	useEffect(() => {
		load();
	}, [load]);

	const counts = data?.counts;
	const open = counts ? counts.new + counts.in_progress : 0;

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Monitoring · Reported issues"
				title={`${open.toLocaleString("en-US")} open reports.`}
				qualifier="What shoppers say is wrong."
				description="Sent from the “Meld en fejl” button on product and category pages. Spam is held back by a signed form token, a hidden field and per-IP limits; what still gets through can be marked as spam per sender."
			/>

			<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex flex-wrap items-center gap-2">
					{(["", ...STATUSES] as const).map((s) => (
						<button
							key={s || "all"}
							type="button"
							onClick={() => {
								setStatus(s);
								setPage(1);
							}}
							className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
								status === s
									? "border-primary bg-primary text-white"
									: "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
							}`}
						>
							{s && (
								<span className={`size-1.5 rounded-full ${STATUS_DOT[s]}`} />
							)}
							{s ? STATUS_LABEL[s] : "All"}
							{s && counts && (
								<span className="font-normal opacity-70">{counts[s]}</span>
							)}
						</button>
					))}
				</div>

				<div className="flex flex-wrap items-center gap-2">
					<select
						value={pageType}
						onChange={(e) => {
							setPageType(e.target.value);
							setPage(1);
						}}
						className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-600 outline-none"
					>
						<option value="">All pages</option>
						<option value="product">Product pages</option>
						<option value="category">Category pages</option>
					</select>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							setPage(1);
							setSearch(searchInput);
						}}
						className="relative w-72"
					>
						<Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-zinc-400" />
						<input
							type="text"
							placeholder="Search message, page, email…"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							className="w-full rounded-xl border border-zinc-200 bg-white py-2 pr-8 pl-10 text-sm outline-none focus:border-zinc-400"
						/>
						{search && (
							<button
								type="button"
								onClick={() => {
									setSearch("");
									setSearchInput("");
									setPage(1);
								}}
								className="absolute top-1/2 right-3 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</form>
				</div>
			</div>

			{error && (
				<p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-danger">
					{error}
				</p>
			)}

			{loading && !data ? (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-3 border-primary/20 border-t-primary" />
				</div>
			) : data && data.items.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-20 text-zinc-400">
					<Flag className="mb-4 h-14 w-14 text-zinc-300" />
					<p className="text-lg font-semibold text-zinc-500">No reports here</p>
					<p className="text-sm">
						{status === "new"
							? "Nothing waiting. New reports show up here."
							: "Try another status or search."}
					</p>
				</div>
			) : (
				<div className="space-y-3">
					{data?.items.map((report) => (
						<ReportCard
							key={`${report.id}-${report.status}-${report.adminNote ?? ""}`}
							report={report}
							onChanged={load}
						/>
					))}
				</div>
			)}

			{data && data.totalPages > 1 && (
				<div className="flex items-center justify-between">
					<p className="text-xs text-zinc-400">
						Page {data.page} of {data.totalPages} · {data.total} reports
					</p>
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={page === 1}
							className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 disabled:pointer-events-none disabled:opacity-40"
						>
							<ChevronLeft className="h-4 w-4" />
						</button>
						<button
							type="button"
							onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
							disabled={page === data.totalPages}
							className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 disabled:pointer-events-none disabled:opacity-40"
						>
							<ChevronRight className="h-4 w-4" />
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default IssueReports;
